import { useMediaClient } from '@databases/media-client';
import type { DatabaseFieldType, RawDatabaseValue } from '@databases/types/database';
import { generateId } from '@databases/utils/utils';
import { useCallback } from 'react';

type Value = RawDatabaseValue<DatabaseFieldType.ATTACHMENT>[0];

/**
 * Creates a new placeholder value for attachment uploads
 */
export const createNewAttachmentValue = (): Value => ({
  fileId: '',
  _id: generateId()
});

/**
 * Interface for attachment upload handlers
 */
export interface AttachmentUploadHandlers {
  onDropNewFile: (file: File, index: number) => Promise<string | undefined>;
  handleDroppedFiles: (acceptedFiles: File[]) => void;
}

/**
 * Interface for attachment upload options
 */
export interface UseAttachmentUploadOptions {
  contentId: string;
  arrayValue: Value[];
  mediaCollectionId: string | null;
  setUploadOrder?: (order: number[] | ((prev: number[]) => number[])) => void;
  handleAddFile: (value: Value) => void;
  handleChangeFile: (value: Value, index: number) => void;
}

/**
 * Shared hook for handling attachment uploads and file operations.
 * Consolidates the duplicate upload logic across attachment components.
 */
export const useAttachmentUpload = ({
  contentId,
  arrayValue,
  mediaCollectionId,
  setUploadOrder,
  handleAddFile,
  handleChangeFile
}: UseAttachmentUploadOptions): AttachmentUploadHandlers => {
  const mediaClient = useMediaClient(contentId);

  const onDropNewFile = useCallback(
    async (file: File, index: number): Promise<string | undefined> => {
      setUploadOrder?.((prevIsUploading) => [...prevIsUploading, index]);
      try {
        if (mediaCollectionId) {
          const fileId = await new Promise<string>((resolve, reject) => {
            const subscription = mediaClient?.file
              .upload({
                content: file,
                name: file.name,
                size: file.size,
                mimeType: file.type,
                collection: mediaCollectionId
              })
              .subscribe((v: any) => {
                if (v.status === 'processing' || v.status === 'processed') {
                  subscription?.unsubscribe();
                  resolve(v.id);
                } else if (v.status === 'error' || v.status === 'failed-processing') {
                  subscription?.unsubscribe();
                  reject(new Error(`Error uploading media: ${v.message}`));
                }
              });
          });
          return fileId;
        }
      } catch (err) {
        return;
      } finally {
        setUploadOrder?.((prevIsUploading) =>
          prevIsUploading.filter((uploadIndex) => uploadIndex !== index)
        );
      }
    },
    [mediaClient, mediaCollectionId, setUploadOrder]
  );

  const handleDroppedFiles = useCallback(
    (acceptedFiles: File[]) => {
      const index = arrayValue.length;
      const placeholderValue = createNewAttachmentValue();
      handleAddFile(placeholderValue);

      acceptedFiles.forEach(async (file: File, i: number) => {
        const fileId = await onDropNewFile(file, index);
        if (fileId) {
          const attachmentValue = {
            _id: arrayValue[index]?._id ?? generateId(),
            fileId,
            filename: file.name
          };

          if (i < 1) {
            handleChangeFile(attachmentValue, index);
          } else {
            handleAddFile(attachmentValue);
          }
        }
      });
    },
    [handleAddFile, handleChangeFile, onDropNewFile, arrayValue]
  );

  return {
    onDropNewFile,
    handleDroppedFiles
  };
};
